import React from 'react';
import type { PlateElementProps, PlateLeafProps } from '@udecode/plate/react';
import type { TElement, TText } from '@udecode/plate';

// Basic text rendering component
export const TextStatic: React.FC<{ text: TText }> = ({ text }) => {
  let content: React.ReactNode = text.text;
  
  // Apply text formatting based on marks
  if (text.bold) content = <strong>{content}</strong>;
  if (text.italic) content = <em>{content}</em>;
  if (text.underline) content = <u>{content}</u>;
  if (text.strikethrough) content = <s>{content}</s>;
  if (text.highlight) content = <mark className="bg-yellow-200 text-black px-1 rounded">{content}</mark>;
  
  return <>{content}</>;
};

// Paragraph component
export const ParagraphElementStatic: React.FC<PlateElementProps> = ({ 
  children, 
  element 
}) => (
  <p className="mb-4" {...(element as TElement & { attributes?: Record<string, unknown> }).attributes}>
    {children}
  </p>
);

// Heading components
export const HeadingElementStatic: React.FC<PlateElementProps & { variant: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' }> = ({ 
  children, 
  element,
  variant = 'h1'
}) => {
  const HeadingTag = variant;
  const classNames = {
    h1: "mb-4 mt-6 text-3xl font-semibold tracking-tight lg:text-4xl",
    h2: "mb-4 mt-6 text-2xl font-semibold tracking-tight",
    h3: "mb-4 mt-6 text-xl font-semibold tracking-tight",
    h4: "mb-4 mt-4 text-lg font-semibold tracking-tight",
    h5: "mb-4 mt-4 text-base font-semibold tracking-tight",
    h6: "mb-4 mt-4 text-sm font-semibold tracking-tight"
  };

  return (
    <HeadingTag className={classNames[variant]} {...(element as TElement & { attributes?: Record<string, unknown> }).attributes}>
      {children}
    </HeadingTag>
  );
};

// Blockquote component
export const BlockquoteElementStatic: React.FC<PlateElementProps> = ({ 
  children, 
  element 
}) => (
  <blockquote 
    className="mb-4 border-l-4 border-[#d0d7de] pl-4 text-[#636c76]"
    {...(element as TElement & { attributes?: Record<string, unknown> }).attributes}
  >
    {children}
  </blockquote>
);

// List components
export const UnorderedListElementStatic: React.FC<PlateElementProps> = ({ 
  children, 
  element 
}) => (
  <ul className="list-disc ml-6 mb-4" {...(element as TElement & { attributes?: Record<string, unknown> }).attributes}>
    {children}
  </ul>
);

export const OrderedListElementStatic: React.FC<PlateElementProps> = ({ 
  children, 
  element 
}) => (
  <ol className="list-decimal ml-6 mb-4" {...(element as TElement & { attributes?: Record<string, unknown> }).attributes}>
    {children}
  </ol>
);

export const ListItemElementStatic: React.FC<PlateElementProps> = ({ 
  children, 
  element 
}) => (
  <li className="mb-1" {...(element as TElement & { attributes?: Record<string, unknown> }).attributes}>
    {children}
  </li>
);

// Link component
export const LinkElementStatic: React.FC<PlateElementProps> = ({ 
  children, 
  element 
}) => {
  const linkElement = element as TElement & { url?: string };
  return (
    <a 
      href={linkElement.url || '#'}
      className="text-blue-600 hover:text-blue-800 hover:underline"
      target="_blank"
      rel="noopener noreferrer"
      {...(element as TElement & { attributes?: Record<string, unknown> }).attributes}
    >
      {children}
    </a>
  );
};

// Image component
export const ImageElementStatic: React.FC<PlateElementProps> = ({ 
  element 
}) => {
  const imageElement = element as TElement & { 
    url?: string; 
    alt?: string; 
    width?: number; 
    height?: number;
    caption?: string;
  };
  
  // Handle img element type specifically
  if (!imageElement.url) {
    return null;
  }
  
  return (
    <img
      src={imageElement.url}
      alt={imageElement.alt || 'Image'}
      className="max-w-full h-auto"
      style={{
        ...(imageElement.width && { width: imageElement.width }),
        ...(imageElement.height && { height: imageElement.height })
      }}
    />
  );
};

// Media embed component
export const MediaEmbedElementStatic: React.FC<PlateElementProps> = ({ 
  element 
}) => {
  const mediaElement = element as TElement & { 
    url?: string;
    source?: string;
  };
  
  if (!mediaElement.url) return null;

  // Handle different media types
  const isYouTube = mediaElement.url.includes('youtube.com') || mediaElement.url.includes('youtu.be');
  const isVimeo = mediaElement.url.includes('vimeo.com');

  if (isYouTube) {
    // Extract YouTube video ID and create embed URL
    const videoId = mediaElement.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
    if (videoId) {
      return (
        <div className="aspect-video">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            className="w-full h-full"
            allowFullScreen
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        </div>
      );
    }
  }

  if (isVimeo) {
    // Extract Vimeo video ID and create embed URL
    const videoId = mediaElement.url.match(/vimeo\.com\/(\d+)/)?.[1];
    if (videoId) {
      return (
        <div className="aspect-video">
          <iframe
            src={`https://player.vimeo.com/video/${videoId}`}
            className="w-full h-full"
            allowFullScreen
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
          />
        </div>
      );
    }
  }

  // Fallback for other media types
  return (
    <div className="p-4 border border-gray-200 rounded bg-gray-50">
      <p className="text-sm text-gray-600 mb-2">Embedded Media:</p>
      <a 
        href={mediaElement.url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 hover:underline break-all"
      >
        {mediaElement.url}
      </a>
    </div>
  );
};

// Table components
export const TableElementStatic: React.FC<PlateElementProps> = ({ 
  children 
}) => (
  <table className="border-collapse border border-gray-300 w-full">
    {children}
  </table>
);

export const TableRowElementStatic: React.FC<PlateElementProps> = ({ 
  children 
}) => (
  <tr>
    {children}
  </tr>
);

export const TableCellElementStatic: React.FC<PlateElementProps> = ({ 
  children 
}) => (
  <td className="border border-gray-300 px-2 py-1">
    {children}
  </td>
);

export const TableCellHeaderElementStatic: React.FC<PlateElementProps> = ({ 
  children 
}) => (
  <th className="border border-gray-300 px-2 py-1 bg-gray-100 font-bold">
    {children}
  </th>
);

// Basic mark components (for leaf nodes)
export const BoldLeafStatic: React.FC<PlateLeafProps> = ({ children }) => (
  <strong>{children}</strong>
);

export const ItalicLeafStatic: React.FC<PlateLeafProps> = ({ children }) => (
  <em>{children}</em>
);

export const UnderlineLeafStatic: React.FC<PlateLeafProps> = ({ children }) => (
  <u>{children}</u>
);

export const StrikethroughLeafStatic: React.FC<PlateLeafProps> = ({ children }) => (
  <s>{children}</s>
);

export const HighlightLeafStatic: React.FC<PlateLeafProps> = ({ children }) => (
  <mark className="bg-yellow-200 text-black px-1 rounded">{children}</mark>
);

// Code components
export const CodeLeafStatic: React.FC<PlateLeafProps> = ({ children }) => (
  <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm font-mono">
    {children}
  </code>
);

export const CodeBlockElementStatic: React.FC<PlateElementProps> = ({ 
  children, 
  element 
}) => {
  const codeElement = element as TElement & { lang?: string };
  
  return (
    <pre 
      className="my-4 bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto"
      {...(element as TElement & { attributes?: Record<string, unknown> }).attributes}
    >
      <code className={codeElement.lang ? `language-${codeElement.lang}` : ''}>
        {children}
      </code>
    </pre>
  );
};